import {
  type AssetResolver,
  BUILT_IN_THEMES,
  builtInResolver,
  chainResolvers,
  type ResolvedTheme,
  resolveTheme,
  type Skin,
  SkinSchema,
  skinAssetKeys,
  type ThemeManifest,
  WITCHER_STRINGS,
  WITCHER_THEME,
} from '@gwent/themes';
import { get, set } from 'idb-keyval';
import { defineStore } from 'pinia';
import { computed, ref, toRaw, watchEffect } from 'vue';
import { setReducedMotion } from '../game/scene/motion';

/**
 * Appearance: the chosen built-in theme, the ordered list of user-imported skins (each with its
 * asset files) and the motion preference. Persisted per device in IndexedDB under one key; asset
 * files are stored as Blobs next to their skin and served to the DOM and the Pixi scene as blob URLs
 * through a chained `AssetResolver` (skins first, later skins win, then the built-in `/themes/<id>/`).
 */

export const APPEARANCE_KEY = 'gwent.appearance.v1';

export type MotionPreference = 'system' | 'on' | 'off';
const MOTION_PREFERENCES: readonly MotionPreference[] = ['system', 'on', 'off'];

export interface SkinEntry {
  skin: Skin;
  enabled: boolean;
  /** Asset keys the skin ships files for (the Blobs themselves stay out of the reactive state). */
  assetKeys: string[];
}

export interface SkinPackInput {
  skin: Skin;
  assets: ReadonlyMap<string, Blob>;
}

interface StoredSkin {
  skin: Skin;
  enabled: boolean;
  assets: Record<string, Blob>;
}

interface StoredAppearance {
  version: 1;
  themeId: string;
  skins: StoredSkin[];
  reducedMotion: MotionPreference;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isBlob = (v: unknown): v is Blob => typeof Blob !== 'undefined' && v instanceof Blob;

/** Lenient decode: a corrupt entry drops that skin, not the whole preference set. */
function decodeStored(value: unknown): StoredAppearance | null {
  if (!isRecord(value) || value.version !== 1) return null;
  const themeId =
    typeof value.themeId === 'string' && value.themeId in BUILT_IN_THEMES ? value.themeId : 'witcher';
  const reducedMotion = MOTION_PREFERENCES.includes(value.reducedMotion as MotionPreference)
    ? (value.reducedMotion as MotionPreference)
    : 'system';
  const skins: StoredSkin[] = [];
  for (const raw of Array.isArray(value.skins) ? value.skins : []) {
    if (!isRecord(raw)) continue;
    const parsed = SkinSchema.safeParse(raw.skin);
    if (!parsed.success) continue;
    const assets: Record<string, Blob> = {};
    if (isRecord(raw.assets)) {
      for (const [key, blob] of Object.entries(raw.assets)) if (isBlob(blob)) assets[key] = blob;
    }
    skins.push({ skin: parsed.data, enabled: raw.enabled !== false, assets });
  }
  return { version: 1, themeId, skins, reducedMotion };
}

/** Pushes the theme's UI palette and fonts into CSS variables so Vue and Pixi read the same values. */
function applyCssVariables(manifest: ThemeManifest): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement.style;
  for (const [key, value] of Object.entries(manifest.colors.ui)) root.setProperty(`--ui-${key}`, value);
  for (const [key, value] of Object.entries(manifest.fonts)) root.setProperty(`--font-${key}`, value);
  for (const [faction, palette] of Object.entries(manifest.colors.factions)) {
    root.setProperty(`--faction-${faction}`, palette.primary);
    root.setProperty(`--faction-${faction}-frame`, palette.frame);
  }
}

function applyMotionPreference(pref: MotionPreference): void {
  setReducedMotion(pref === 'system' ? null : pref === 'on');
  if (typeof document !== 'undefined') document.documentElement.dataset.motion = pref;
}

/**
 * The Pixi scene bakes card faces through `FaceFactory`, which asks a module-level shared resolver
 * before its own. It lives in the match chunk, so it is loaded lazily and only in the browser.
 */
let sceneModule: Promise<typeof import('../game/scene/CardSprite')> | null = null;
function publishSceneResolver(resolver: AssetResolver): void {
  if (typeof window === 'undefined') return;
  sceneModule ??= import('../game/scene/CardSprite');
  sceneModule
    .then((m) => m.setSharedAssetResolver(resolver))
    .catch(() => {
      // The match chunk failed to load; the scene falls back to the built-in resolver.
    });
}

export const useThemeStore = defineStore('theme', () => {
  const themeId = ref('witcher');
  /** Skins stack in order; later entries win. Only enabled ones take part in resolution. */
  const skins = ref<SkinEntry[]>([]);
  const reducedMotion = ref<MotionPreference>('system');
  /** True once IndexedDB has been consulted (whether or not preferences were found). */
  const ready = ref(false);
  /** Set when IndexedDB refused the last write; the in-memory preferences still apply to this tab. */
  const persistError = ref<string | null>(null);

  /** skin id → asset key → file. Not reactive: Blobs are opaque and never change. */
  const assetBlobs = new Map<string, Map<string, Blob>>();
  /** skin id → asset key → blob URL, created on first use and revoked when the skin goes away. */
  const objectUrls = new Map<string, Map<string, string>>();
  let loading: Promise<void> | null = null;

  const themeOptions = computed(() =>
    Object.entries(BUILT_IN_THEMES).map(([id, t]) => ({ id, name: t.manifest.name })),
  );

  const enabledSkins = computed(() => skins.value.filter((s) => s.enabled).map((s) => s.skin));

  const resolved = computed<ResolvedTheme>(() => {
    const base = BUILT_IN_THEMES[themeId.value] ?? { manifest: WITCHER_THEME, strings: WITCHER_STRINGS };
    return resolveTheme(base, enabledSkins.value);
  });

  function skinResolver(skinId: string): AssetResolver {
    return (key) => {
      const blob = assetBlobs.get(skinId)?.get(key);
      if (!blob) return undefined;
      let urls = objectUrls.get(skinId);
      if (!urls) {
        urls = new Map();
        objectUrls.set(skinId, urls);
      }
      let url = urls.get(key);
      if (!url) {
        url = URL.createObjectURL(blob);
        urls.set(key, url);
      }
      return url;
    };
  }

  /** Maps theme-relative asset keys to URLs: enabled skins (last one first), then the built-in theme. */
  const resolver = computed<AssetResolver>(() => {
    const layers = skins.value
      .filter((s) => s.enabled)
      .map((s) => skinResolver(s.skin.id))
      .reverse();
    return chainResolvers(...layers, builtInResolver(resolved.value.manifest.id));
  });

  function assetUrl(key: string): string | undefined {
    return resolver.value(key);
  }

  function revokeUrls(skinId: string): void {
    const urls = objectUrls.get(skinId);
    if (!urls) return;
    for (const url of urls.values()) URL.revokeObjectURL(url);
    objectUrls.delete(skinId);
  }

  watchEffect(() => applyCssVariables(resolved.value.manifest));
  watchEffect(() => applyMotionPreference(reducedMotion.value));
  watchEffect(() => publishSceneResolver(resolver.value));

  /** Reads the persisted preferences once; safe to call from every view. */
  function load(): Promise<void> {
    loading ??= (async () => {
      try {
        const stored = decodeStored(await get(APPEARANCE_KEY));
        if (stored) {
          themeId.value = stored.themeId;
          reducedMotion.value = stored.reducedMotion;
          for (const id of assetBlobs.keys()) revokeUrls(id);
          assetBlobs.clear();
          skins.value = stored.skins.map((s) => {
            assetBlobs.set(s.skin.id, new Map(Object.entries(s.assets)));
            return { skin: s.skin, enabled: s.enabled, assetKeys: Object.keys(s.assets) };
          });
        }
      } catch {
        // IndexedDB unavailable (private mode, jsdom): defaults for this session.
      } finally {
        ready.value = true;
      }
    })();
    return loading;
  }

  async function persist(): Promise<void> {
    // Plain copies only: IndexedDB structured-clones the value and rejects Vue's reactive proxies.
    const plain: StoredAppearance = {
      version: 1,
      themeId: themeId.value,
      reducedMotion: reducedMotion.value,
      skins: skins.value.map((entry) => ({
        skin: JSON.parse(JSON.stringify(toRaw(entry.skin))) as Skin,
        enabled: entry.enabled,
        assets: Object.fromEntries(assetBlobs.get(entry.skin.id) ?? []),
      })),
    };
    try {
      await set(APPEARANCE_KEY, plain);
      persistError.value = null;
    } catch (cause) {
      persistError.value = cause instanceof Error ? cause.message : String(cause);
    }
  }

  function setTheme(id: string): Promise<void> {
    themeId.value = id in BUILT_IN_THEMES ? id : 'witcher';
    return persist();
  }

  function setMotion(pref: MotionPreference): Promise<void> {
    reducedMotion.value = pref;
    return persist();
  }

  /** Adds a parsed pack. A skin with the same id is replaced in place, keeping its position and state. */
  function addSkin(pack: SkinPackInput): Promise<void> {
    const id = pack.skin.id;
    const wanted = new Set(skinAssetKeys(pack.skin));
    const files = new Map<string, Blob>();
    for (const [key, blob] of pack.assets) if (wanted.has(key)) files.set(key, blob);
    revokeUrls(id);
    assetBlobs.set(id, files);
    const entry: SkinEntry = { skin: pack.skin, enabled: true, assetKeys: [...files.keys()] };
    const index = skins.value.findIndex((s) => s.skin.id === id);
    if (index >= 0) {
      const existing = skins.value[index];
      skins.value.splice(index, 1, { ...entry, enabled: existing?.enabled ?? true });
    } else skins.value.push(entry);
    return persist();
  }

  function removeSkin(id: string): Promise<void> {
    const index = skins.value.findIndex((s) => s.skin.id === id);
    if (index < 0) return Promise.resolve();
    skins.value.splice(index, 1);
    revokeUrls(id);
    assetBlobs.delete(id);
    return persist();
  }

  function setSkinEnabled(id: string, enabled: boolean): Promise<void> {
    const entry = skins.value.find((s) => s.skin.id === id);
    if (!entry || entry.enabled === enabled) return Promise.resolve();
    entry.enabled = enabled;
    return persist();
  }

  /** Moves a skin one step towards the top (-1, applied earlier) or the bottom (+1, wins over the rest). */
  function moveSkin(id: string, direction: -1 | 1): Promise<void> {
    const index = skins.value.findIndex((s) => s.skin.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= skins.value.length) return Promise.resolve();
    const list = skins.value;
    const [entry] = list.splice(index, 1);
    if (entry) list.splice(target, 0, entry);
    return persist();
  }

  return {
    themeId,
    skins,
    reducedMotion,
    ready,
    persistError,
    themeOptions,
    resolved,
    resolver,
    assetUrl,
    load,
    setTheme,
    setMotion,
    addSkin,
    removeSkin,
    setSkinEnabled,
    moveSkin,
  };
});
